import React, { useState, useMemo } from 'react';
import {
  Container,
  Card,
  CardContent,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  AlertTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Stack,
  TextField,
  InputAdornment,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import GavelIcon from '@mui/icons-material/Gavel';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ClearIcon from '@mui/icons-material/Clear';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Client } from '@microsoft/microsoft-graph-client';
import { useMsal } from '@azure/msal-react';

interface OAuth2PermissionGrant {
  id: string;
  clientId: string;
  consentType: 'AllPrincipals' | 'Principal';
  principalId: string | null;
  resourceId: string;
  scope: string;
  expiryTime?: string;
}

interface EnrichedGrant extends OAuth2PermissionGrant {
  clientDisplayName?: string;
  resourceDisplayName?: string;
  principalDisplayName?: string;
  principalUpn?: string;
}

interface ConsentManagerProps {
  onBack?: () => void;
}

const ConsentManager: React.FC<ConsentManagerProps> = ({ onBack }) => {
  const { instance, accounts } = useMsal();
  
  // Form state
  const [clientSpId, setClientSpId] = useState('');
  const [resourceSpId, setResourceSpId] = useState('');
  const [microsoftGraphSpId, setMicrosoftGraphSpId] = useState<string>('');
  const [fetchingGraphSpId, setFetchingGraphSpId] = useState(false);
  
  // Data state
  const [grants, setGrants] = useState<EnrichedGrant[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [aboutExpanded, setAboutExpanded] = useState(false);
  // Track if user has executed at least one fetch to control empty-state rendering
  const [hasFetched, setHasFetched] = useState(false);
  
  // Graph token state
  const [graphToken, setGraphToken] = useState<string>('');
  
  // Delete confirmation dialog
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [grantToDelete, setGrantToDelete] = useState<EnrichedGrant | null>(null);

  // *** UPDATED: Enhanced scope handling for read and write operations ***
  // Acquire Graph token with Directory.Read.All (for viewing) or Directory.ReadWrite.All (for deleting)
  const acquireGraphToken = async (requireWriteAccess: boolean = false): Promise<string | null> => {
    if (accounts.length === 0) {
      setError('No authenticated user found. Please sign in first.');
      return null;
    }

    // *** UPDATED: Request appropriate scopes based on operation ***
    const scopes = requireWriteAccess 
      ? ['Directory.ReadWrite.All']  // Required for deleting grants
      : ['Directory.Read.All'];       // Sufficient for viewing grants

    const request = {
      scopes,
      account: accounts[0],
    };

    try {
      const response = await instance.acquireTokenSilent(request);
      return response.accessToken;
    } catch (error: any) {
      if (error.name === 'InteractionRequiredAuthError') {
        try {
          const response = await instance.acquireTokenPopup(request);
          return response.accessToken;
        } catch (popupError: any) {
          console.error('Error acquiring Graph token:', popupError);
          // *** UPDATED: Clearer error message based on operation ***
          const requiredScope = requireWriteAccess ? 'Directory.ReadWrite.All' : 'Directory.Read.All';
          setError(`Failed to acquire Graph API token. You may not have the required permission: ${requiredScope}`);
          return null;
        }
      } else {
        console.error('Error acquiring Graph token:', error);
        setError('Failed to acquire Graph API token. Error: ' + error.message);
        return null;
      }
    }
  };

  // Fetch Microsoft Graph Service Principal ID on mount
  React.useEffect(() => {
    const fetchGraphSpId = async () => {
      setFetchingGraphSpId(true);
      try {
        // *** UPDATED: Use read-only token for initial fetch ***
        const token = await acquireGraphToken(false);
        if (!token) return;

        const client = Client.init({
          authProvider: (done) => done(null, token),
        });

        // Query for Microsoft Graph service principal by appId
        const result = await client
          .api('/servicePrincipals')
          .filter("appId eq '00000003-0000-0000-c000-000000000000'")
          .select('id,displayName')
          .get();

        if (result.value && result.value.length > 0) {
          const graphSpId = result.value[0].id;
          setMicrosoftGraphSpId(graphSpId);
          setResourceSpId(graphSpId); // Set as default
        }
      } catch (err) {
        console.error('Error fetching Microsoft Graph SP ID:', err);
      } finally {
        setFetchingGraphSpId(false);
      }
    };

    fetchGraphSpId();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only run once on mount

  // Handle browser back button
  React.useEffect(() => {
    const handlePopState = () => {
      if (onBack) {
        onBack();
      }
    };

    window.addEventListener('popstate', handlePopState);
    
    // Push a state to enable back button navigation
    window.history.pushState(null, '', window.location.href);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [onBack]);

  const handleFetchGrants = async () => {
    // *** UPDATED: Acquire token with read-only permissions for fetching ***
    const token = await acquireGraphToken(false);
    if (!token) return;
    
    setGraphToken(token);
    setHasFetched(true);
    await fetchGrants(token);
  };

  const fetchGrants = async (token: string) => {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const client = Client.init({
        authProvider: (done) => done(null, token),
      });

      // Build filter based on form inputs
      const filters: string[] = [];
      if (clientSpId.trim()) filters.push(`clientId eq '${clientSpId.trim()}'`);
      if (resourceSpId.trim()) filters.push(`resourceId eq '${resourceSpId.trim()}'`);

      const filterString = filters.length > 0 ? `?$filter=${filters.join(' and ')}` : '';

      // Fetch grants
      const grantsResp = await client
        .api(`/oauth2PermissionGrants${filterString}`)
        .get();

      const grantsData: OAuth2PermissionGrant[] = grantsResp.value || [];

      // Enrich with display names
      const enriched = await enrichGrants(client, grantsData);
      setGrants(enriched);

    } catch (err: any) {
      console.error('Error fetching grants:', err);
      setError(err.message || 'Failed to fetch permission grants');
    } finally {
      setLoading(false);
    }
  };

  const enrichGrants = async (
    client: any,
    grantsData: OAuth2PermissionGrant[]
  ): Promise<EnrichedGrant[]> => {
    const enriched: EnrichedGrant[] = [];

    // Cache for service principals and users to avoid duplicate lookups
    const spCache = new Map<string, { displayName: string; appDisplayName?: string }>();
    const userCache = new Map<string, { displayName: string; userPrincipalName: string }>();

    for (const grant of grantsData) {
      const enrichedGrant: EnrichedGrant = { ...grant };

      try {
        // Get client (app) display name
        if (!spCache.has(grant.clientId)) {
          const clientSp = await client.api(`/servicePrincipals/${grant.clientId}`).get();
          spCache.set(grant.clientId, {
            displayName: clientSp.displayName || clientSp.appDisplayName || grant.clientId,
            appDisplayName: clientSp.appDisplayName
          });
        }
        const clientInfo = spCache.get(grant.clientId);
        enrichedGrant.clientDisplayName = clientInfo?.displayName || grant.clientId;

        // Get resource (API) display name
        if (!spCache.has(grant.resourceId)) {
          const resourceSp = await client.api(`/servicePrincipals/${grant.resourceId}`).get();
          spCache.set(grant.resourceId, {
            displayName: resourceSp.displayName || resourceSp.appDisplayName || grant.resourceId,
            appDisplayName: resourceSp.appDisplayName
          });
        }
        const resourceInfo = spCache.get(grant.resourceId);
        enrichedGrant.resourceDisplayName = resourceInfo?.displayName || grant.resourceId;

        // Get principal (user) display name if user consent
        if (grant.consentType === 'Principal' && grant.principalId) {
          if (!userCache.has(grant.principalId)) {
            try {
              const user = await client.api(`/users/${grant.principalId}`).get();
              userCache.set(grant.principalId, {
                displayName: user.displayName || user.userPrincipalName || grant.principalId,
                userPrincipalName: user.userPrincipalName || ''
              });
            } catch {
              // User might not exist anymore
              userCache.set(grant.principalId, {
                displayName: grant.principalId,
                userPrincipalName: ''
              });
            }
          }
          const userInfo = userCache.get(grant.principalId);
          enrichedGrant.principalDisplayName = userInfo?.displayName;
          enrichedGrant.principalUpn = userInfo?.userPrincipalName;
        }

      } catch (err) {
        console.warn('Error enriching grant:', err);
        // Continue with partial data
      }

      enriched.push(enrichedGrant);
    }

    return enriched;
  };

  const initiateDelete = (grant: EnrichedGrant) => {
    setGrantToDelete(grant);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!grantToDelete) return;

    setDeleteConfirmOpen(false);
    setDeleting(grantToDelete.id);
    setError(null);
    setSuccessMessage(null);

    try {
      // *** UPDATED: Request token with write permissions for delete operation ***
      const token = await acquireGraphToken(true);
      if (!token) {
        setDeleting(null);
        setGrantToDelete(null);
        return;
      }

      const client = Client.init({
        authProvider: (done) => done(null, token),
      });

      await client.api(`/oauth2PermissionGrants/${grantToDelete.id}`).delete();

      setSuccessMessage(`Successfully deleted permission grant for ${grantToDelete.clientDisplayName || 'client'}`);
      
      // *** FIXED: Refresh the list but preserve the current filter state (don't reset resourceSpId) ***
      await fetchGrants(graphToken);

    } catch (err: any) {
      console.error('Error deleting grant:', err);
      
      // *** UPDATED: Enhanced error handling with actionable guidance ***
      let errorMessage = err.message || 'Failed to delete permission grant';
      
      // Check for insufficient privileges error
      if (err.message?.includes('Insufficient privileges') || 
          err.statusCode === 403 || 
          err.code === 'Authorization_RequestDenied') {
        errorMessage = 'Insufficient privileges to delete this grant. You need one of these permissions:\n' +
                      '• Directory.ReadWrite.All (recommended)\n' +
                      '• DelegatedPermissionGrant.ReadWrite.All\n\n' +
                      'Please ask your administrator to grant these permissions to your user account or use an admin account.';
      }
      
      setError(errorMessage);
    } finally {
      setDeleting(null);
      setGrantToDelete(null);
    }
  };

  // Filter grants based on search query
  const filteredGrants = useMemo(() => {
    if (!searchQuery) return grants;

    const query = searchQuery.toLowerCase();
    return grants.filter(grant => 
      grant.clientDisplayName?.toLowerCase().includes(query) ||
      grant.resourceDisplayName?.toLowerCase().includes(query) ||
      grant.principalDisplayName?.toLowerCase().includes(query) ||
      grant.principalUpn?.toLowerCase().includes(query) ||
      grant.scope.toLowerCase().includes(query) ||
      grant.consentType.toLowerCase().includes(query)
    );
  }, [grants, searchQuery]);

  // Group grants by resource for better organization
  const grantsByResource = useMemo(() => {
    const grouped = new Map<string, EnrichedGrant[]>();
    
    filteredGrants.forEach(grant => {
      const key = grant.resourceDisplayName || grant.resourceId;
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key)!.push(grant);
    });

    return Array.from(grouped.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filteredGrants]);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      {/* Header Card */}
      <Card
        variant="outlined"
        sx={{
          mb: 4,
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 2,
            backgroundColor: '#f4f8fb',
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
          }}
        >
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            disabled={!onBack}
            sx={{
              mr: 2,
              borderRadius: 2,
              pl: 1.5,
              color: "text.secondary",
              borderColor: "divider",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.04)",
                borderColor: "primary.main",
                color: "primary.main"
              }
            }}
          >
            Back
          </Button>

          <Box
            sx={{
              borderLeft: '4px solid',
              borderColor: 'primary.main',
              pl: 2,
              py: 0.5,
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: 'primary.main',
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                letterSpacing: '-0.01em',
              }}
            >
              OAuth2 Consent Manager
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
              }}
            >
              View and delete OAuth2 delegated permission grants (consents) for applications
            </Typography>
          </Box>
        </Box>
      </Card>

      {/* Input Form Card */}
      {/* *** UPDATED: Enhanced UI with modern card styling *** */}
      <Card 
        variant="outlined" 
        sx={{ 
          borderRadius: 2, 
          mb: 3,
          background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
          border: '1.5px solid #e2e8f0',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
          }
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            backgroundColor: '#f4f8fb',
            borderBottom: '2px solid',
            borderColor: 'primary.main'
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <SearchIcon color="primary" />
            Filter Parameters
          </Typography>
          <Chip 
            label={grants.length > 0 ? `${grants.length} grant${grants.length !== 1 ? 's' : ''} loaded` : 'Ready'} 
            size="small"
            color={grants.length > 0 ? 'success' : 'default'}
            variant="outlined"
          />
        </Box>
        <CardContent sx={{ p: 3 }}>
          <Stack spacing={2.5}>
            {/* *** UPDATED: Enhanced text field styling *** */}
            <TextField
              label="Client Service Principal ID"
              placeholder="e.g., 1f684027-9f6a-48ba-852b-d051849a79c8"
              value={clientSpId}
              onChange={(e) => setClientSpId(e.target.value)}
              fullWidth
              helperText="The Service Principal (Object) ID of the client application"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': {
                    borderColor: 'primary.main',
                  },
                },
              }}
              InputProps={{
                endAdornment: clientSpId && (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="clear client ID"
                      onClick={() => setClientSpId('')}
                      edge="end"
                      size="small"
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label="Resource Service Principal ID"
              placeholder={microsoftGraphSpId || "Loading..."}
              value={resourceSpId}
              onChange={(e) => setResourceSpId(e.target.value)}
              fullWidth
              helperText={`The Service Principal (Object) ID of the resource API${microsoftGraphSpId ? ' (default: Microsoft Graph)' : ' (loading default...)'}`}
              disabled={fetchingGraphSpId}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': {
                    borderColor: 'primary.main',
                  },
                },
              }}
              InputProps={{
                endAdornment: resourceSpId && (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="clear resource ID"
                      onClick={() => setResourceSpId('')}
                      edge="end"
                      size="small"
                      title="Clear field"
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* *** UPDATED: Enhanced button styling with icons *** */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                onClick={handleFetchGrants}
                disabled={loading || !clientSpId.trim()}
                fullWidth
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                sx={{ 
                  borderRadius: 2,
                  py: 1.25,
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: 2,
                  '&:hover': {
                    boxShadow: 4,
                  }
                }}
              >
                {loading ? 'Loading...' : 'Fetch Permission Grants'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setClientSpId('');
                  setResourceSpId(microsoftGraphSpId);
                  setGrants([]);
                  setError(null);
                  setSuccessMessage(null);
                  setSearchQuery('');
                  setHasFetched(false);
                }}
                disabled={loading}
                startIcon={<ClearIcon />}
                sx={{
                  borderRadius: 2,
                  minWidth: 120,
                  fontWeight: 600,
                  textTransform: 'none',
                }}
              >
                Clear
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Results Card */}
      {/* *** UPDATED: Show results container after first fetch, even when 0 results *** */}
      {(hasFetched || error || loading) && (
        <Card 
          variant="outlined" 
          sx={{ 
            borderRadius: 2,
            background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
            border: '1.5px solid #e2e8f0',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
            }
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              backgroundColor: '#f4f8fb',
              borderBottom: '2px solid',
              borderColor: 'primary.main'
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleIcon color="primary" />
              Permission Grants
              <Chip 
                label={grants.length} 
                size="small" 
                color="primary" 
                sx={{ ml: 1, fontWeight: 600 }}
              />
            </Typography>
            {grants.length > 0 && (
              <Tooltip title="Refresh grant list">
                <IconButton 
                  onClick={handleFetchGrants} 
                  disabled={loading} 
                  size="small"
                  sx={{
                    '&:hover': {
                      backgroundColor: 'primary.light',
                      color: 'white'
                    }
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <CardContent sx={{ p: 3 }}>
            {/* Success Message */}
            {successMessage && (
              <Alert 
                severity="success" 
                sx={{ 
                  mb: 2,
                  borderRadius: 2,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                }} 
                onClose={() => setSuccessMessage(null)}
              >
                {successMessage}
              </Alert>
            )}

            {/* Error Message */}
            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 2,
                  borderRadius: 2,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                }} 
                onClose={() => setError(null)}
              >
                <AlertTitle>Error</AlertTitle>
                {error}
              </Alert>
            )}

            {/* Search Box */}
            {grants.length > 0 && (
              <TextField
                fullWidth
                placeholder="Search by app name, resource, user, scopes, or consent type..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ 
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: '#f8fafc',
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                  }
                }}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="clear search"
                        onClick={() => setSearchQuery('')}
                        edge="end"
                        size="small"
                      >
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}

            {/* Loading State */}
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading permission grants...</Typography>
              </Box>
            )}

            {/* No Grants Found */}
            {!loading && grants.length === 0 && !error && (
              <Box sx={{
                py: 6,
                textAlign: 'center',
                backgroundColor: '#f8f9fa',
                borderRadius: 2,
                border: '2px dashed #dee2e6'
              }}>
                <CheckCircleIcon sx={{ fontSize: 48, color: '#adb5bd', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Permission Grants Found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This application has no OAuth2 permission grants for the specified resource.
                </Typography>
              </Box>
            )}

            {/* Grants List - Grouped by Resource */}
            {!loading && filteredGrants.length > 0 && (
              <Box>
                {/* *** UPDATED: Enhanced search results summary with better styling *** */}
                <Box sx={{ 
                  mb: 3, 
                  p: 2, 
                  backgroundColor: '#f8fafc', 
                  borderRadius: 2,
                  border: '1px solid #e2e8f0',
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center' 
                }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SearchIcon fontSize="small" />
                    Found {filteredGrants.length} permission grant{filteredGrants.length !== 1 ? 's' : ''}
                    {searchQuery && <Chip label={`"${searchQuery}"`} size="small" variant="outlined" />}
                  </Typography>
                </Box>

                {/* *** UPDATED: Enhanced resource groups with better visual hierarchy *** */}
                {grantsByResource.map(([resourceName, resourceGrants]) => (
                  <Box 
                    key={resourceName} 
                    sx={{ 
                      mb: 4,
                      p: 2,
                      borderRadius: 2,
                      backgroundColor: '#fafbfc',
                      border: '1px solid #e8eef3'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                        {resourceName}
                      </Typography>
                      <Chip 
                        label={`${resourceGrants.length} grant${resourceGrants.length !== 1 ? 's' : ''}`} 
                        size="small" 
                        color="primary"
                        variant="outlined"
                      />
                    </Box>
                    
                    {/* *** UPDATED: Enhanced table with modern styling and better visual hierarchy *** */}
                    <TableContainer 
                      component={Paper} 
                      variant="outlined" 
                      sx={{ 
                        borderRadius: 2,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                        border: '1px solid #e8eef3'
                      }}
                    >
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: '#f0f4f8' }}>
                            <TableCell sx={{ fontWeight: 700, color: '#334155' }}>Client App</TableCell>
                            <TableCell sx={{ fontWeight: 700, color: '#334155' }}>Consent Type</TableCell>
                            <TableCell sx={{ fontWeight: 700, color: '#334155' }}>User</TableCell>
                            <TableCell sx={{ fontWeight: 700, color: '#334155' }}>Scopes</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700, color: '#334155' }}>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {resourceGrants.map((grant) => (
                            <TableRow 
                              key={grant.id}
                              sx={{ 
                                '&:hover': { backgroundColor: '#f8fafc' },
                                opacity: deleting === grant.id ? 0.5 : 1,
                                transition: 'all 0.2s ease',
                                borderBottom: '1px solid #e8eef3'
                              }}
                            >
                              <TableCell>
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                                    {grant.clientDisplayName || grant.clientId}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: '#64748b', fontFamily: 'monospace' }}>
                                    {grant.clientId}
                                  </Typography>
                                </Box>
                              </TableCell>
                              
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={grant.consentType === 'AllPrincipals' ? 'Admin' : 'User'}
                                  color={grant.consentType === 'AllPrincipals' ? 'primary' : 'default'}
                                  sx={{ 
                                    fontWeight: 600,
                                    borderRadius: 1.5,
                                    height: 24
                                  }}
                                />
                              </TableCell>
                              
                              <TableCell>
                                {grant.consentType === 'Principal' ? (
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                      {grant.principalDisplayName || grant.principalId}
                                    </Typography>
                                    {grant.principalUpn && (
                                      <Typography variant="caption" sx={{ color: '#64748b' }}>
                                        {grant.principalUpn}
                                      </Typography>
                                    )}
                                  </Box>
                                ) : (
                                  <Chip 
                                    size="small" 
                                    label="All Users" 
                                    variant="outlined"
                                    sx={{ 
                                      borderRadius: 1.5,
                                      fontWeight: 500
                                    }}
                                  />
                                )}
                              </TableCell>
                              
                              <TableCell>
                                {/* *** UPDATED: Enhanced scope chips with better styling *** */}
                                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', maxWidth: 400 }}>
                                  {grant.scope?.split(' ').filter(Boolean).map((scope, idx) => (
                                    <Chip 
                                      key={idx} 
                                      label={scope} 
                                      size="small" 
                                      variant="outlined"
                                      sx={{
                                        borderRadius: 1.5,
                                        fontSize: '0.7rem',
                                        height: 22,
                                        backgroundColor: '#f0f4f8',
                                        borderColor: '#cbd5e1',
                                        '&:hover': {
                                          backgroundColor: '#e2e8f0',
                                          borderColor: '#94a3b8'
                                        }
                                      }}
                                    />
                                  ))}
                                </Box>
                              </TableCell>
                              
                              <TableCell align="center">
                                {/* *** UPDATED: Enhanced delete button with better visual feedback *** */}
                                <Tooltip title="Delete this permission grant">
                                  <span>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => initiateDelete(grant)}
                                      disabled={deleting === grant.id}
                                      sx={{
                                        '&:hover': {
                                          backgroundColor: 'error.light',
                                          color: 'white'
                                        },
                                        transition: 'all 0.2s ease'
                                      }}
                                    >
                                      {deleting === grant.id ? (
                                        <CircularProgress size={20} color="error" />
                                      ) : (
                                        <DeleteIcon fontSize="small" />
                                      )}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                ))}
              </Box>
            )}

            {/* No Results from Search */}
            {!loading && grants.length > 0 && filteredGrants.length === 0 && (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <SearchIcon sx={{ fontSize: 48, color: '#adb5bd', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Results Found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Try adjusting your search query
                </Typography>
              </Box>
            )}

            {/* Warning about deleting admin consent */}
            {/* *** UPDATED: Enhanced warning with better styling *** */}
            {filteredGrants.some(g => g.consentType === 'AllPrincipals') && (
              <Alert 
                severity="warning" 
                sx={{ 
                  mt: 3,
                  borderRadius: 2,
                  border: '1px solid #fbbf24',
                  backgroundColor: '#fef3c7'
                }} 
                icon={<WarningIcon />}
              >
                <AlertTitle sx={{ fontWeight: 600 }}>Note about Admin Consent</AlertTitle>
                <Typography variant="body2">
                  Deleting admin consent (AllPrincipals) will require an administrator to re-grant consent 
                  before users can use the application with those permissions.
                </Typography>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation Dialog */}
      {/* *** UPDATED: Enhanced dialog with modern styling *** */}
      <Dialog 
        open={deleteConfirmOpen} 
        onClose={() => setDeleteConfirmOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            minWidth: 500
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1, 
          fontWeight: 600,
          fontSize: '1.25rem',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <WarningIcon color="warning" />
          Confirm Delete Permission Grant
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Alert 
            severity="warning" 
            icon={<WarningIcon />} 
            sx={{ 
              mb: 2,
              borderRadius: 2,
              backgroundColor: '#fef3c7',
              border: '1px solid #fbbf24'
            }}
          >
            <AlertTitle sx={{ fontWeight: 600 }}>Are you sure?</AlertTitle>
            <Typography variant="body2">
              This will permanently delete the following OAuth2 permission grant:
            </Typography>
          </Alert>
          
          {grantToDelete && (
            <Box sx={{ 
              mt: 2, 
              p: 2.5, 
              backgroundColor: '#f8fafc', 
              borderRadius: 2,
              border: '1px solid #e2e8f0'
            }}>
              <Stack spacing={1.5}>
                <Box>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                    CLIENT APPLICATION
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.5 }}>
                    {grantToDelete.clientDisplayName || grantToDelete.clientId}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                    RESOURCE API
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.5 }}>
                    {grantToDelete.resourceDisplayName || grantToDelete.resourceId}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                    CONSENT TYPE
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      size="small"
                      label={grantToDelete.consentType === 'AllPrincipals' ? 'Admin (All Users)' : 'User (Individual)'}
                      color={grantToDelete.consentType === 'AllPrincipals' ? 'primary' : 'default'}
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>
                </Box>
                {grantToDelete.principalDisplayName && (
                  <Box>
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                      USER
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.5 }}>
                      {grantToDelete.principalDisplayName}
                    </Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                    SCOPES
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.5 }}>
                    {grantToDelete.scope?.split(' ').filter(Boolean).map((scope, idx) => (
                      <Chip 
                        key={idx} 
                        label={scope} 
                        size="small" 
                        variant="outlined"
                        sx={{
                          borderRadius: 1.5,
                          fontSize: '0.7rem',
                          backgroundColor: '#f0f4f8'
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              </Stack>
            </Box>
          )}
          
          <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
            <Typography variant="body2">
              Users will need to re-consent when they next use the application with these permissions.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button 
            onClick={() => setDeleteConfirmOpen(false)}
            sx={{ 
              borderRadius: 2,
              fontWeight: 600,
              textTransform: 'none'
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmDelete} 
            color="error" 
            variant="contained"
            sx={{ 
              borderRadius: 2,
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: 2,
              '&:hover': {
                boxShadow: 4
              }
            }}
          >
            Delete Grant
          </Button>
        </DialogActions>
      </Dialog>

      {/* About Section - Expandable */}
      <Card variant="outlined" sx={{ borderRadius: 2, mb: 3, mt: 3 }}>
        <Accordion expanded={aboutExpanded} onChange={() => setAboutExpanded(!aboutExpanded)}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <InfoOutlinedIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                About OAuth2 Consent Manager
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                This tool allows you to view and manage OAuth2 delegated permission grants (also known as consents) 
                between client applications and resource APIs in your Microsoft Entra ID tenant. You can filter grants 
                by client and resource service principals, and revoke grants when needed.
              </Typography>
              
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Understanding OAuth2 Permission Grants:
                </Typography>
                <Stack spacing={1}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      OAuth2 permission grants represent the actual consented permissions in your tenant. Each grant connects:
                    </Typography>
                    <Box component="ul" sx={{ mt: 0.5, mb: 0.5, pl: 3 }}>
                      <li><Typography variant="body2" color="text.secondary"><strong>Client:</strong> The application requesting access (identified by its Service Principal Object ID)</Typography></li>
                      <li><Typography variant="body2" color="text.secondary"><strong>Resource:</strong> The API being accessed (e.g., Microsoft Graph, identified by its Service Principal Object ID)</Typography></li>
                      <li><Typography variant="body2" color="text.secondary"><strong>Scope:</strong> The specific permissions granted (e.g., User.Read, Mail.Send)</Typography></li>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <Chip label="Admin Consent" size="small" color="success" sx={{ mr: 1, minWidth: 120, mt: 0.5 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        <strong>AllPrincipals (Admin Consent):</strong> Granted by a tenant administrator, applies to all users in the organization
                      </Typography>
                      <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                        Requires admin privileges to grant or revoke
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <Chip label="User Consent" size="small" color="info" sx={{ mr: 1, minWidth: 120, mt: 0.5 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Principal (User Consent):</strong> Granted by an individual user, applies only to that specific user
                      </Typography>
                      <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                        Each user consents separately for their own account
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  How to Use This Tool:
                </Typography>
                <Stack spacing={0.5}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>1. Enter Client Service Principal ID</strong> - The Object ID (not Application ID) of the client application
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>2. Enter Resource Service Principal ID</strong> - The Object ID of the resource API (defaults to Microsoft Graph)
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>3. Click "Fetch Permission Grants"</strong> - View all matching OAuth2 grants
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>4. Review and Delete</strong> - Click the delete icon to revoke specific grants
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mt: 1 }}>
                    💡 <strong>Tip:</strong> To find a Service Principal Object ID, use the Entra Endpoints Explorer tool or Azure Portal
                  </Typography>
                </Stack>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Required Permissions:
                </Typography>
                <Stack spacing={0.5}>
                  <Typography variant="body2" color="text.secondary">
                    • <strong>Directory.Read.All</strong> - Required to view permission grants, service principals, and users
                  </Typography>
                  {/* *** UPDATED: Clarified delete permission requirements *** */}
                  <Typography variant="body2" color="text.secondary">
                    • <strong>Directory.ReadWrite.All</strong> OR <strong>DelegatedPermissionGrant.ReadWrite.All</strong> - Required to delete grants
                  </Typography>
                  <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block', fontStyle: 'italic' }}>
                    ⚠️ If you see "Insufficient privileges" when deleting, you need Directory.ReadWrite.All or DelegatedPermissionGrant.ReadWrite.All permissions
                  </Typography>
                </Stack>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Important Notes:
                </Typography>
                <Stack spacing={0.5}>
                  <Typography variant="body2" color="text.secondary">
                    • Use <strong>Service Principal Object IDs</strong>, not Application (Client) IDs
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • Microsoft Graph's Service Principal Object ID is automatically fetched and set as the default resource
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • Deleting a grant will require users to re-consent when they next use the application
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • Admin consent grants affect all users; user consent grants affect only specific users
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • This tool manages <strong>delegated permissions</strong> only, not application permissions (app roles)
                  </Typography>
                </Stack>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'warning.main' }}>
                  ⚠️ Common Use Cases:
                </Typography>
                <Stack spacing={0.5}>
                  <Typography variant="body2" color="text.secondary">
                    • <strong>Revoking over-privileged consents:</strong> Remove excessive permissions granted to applications
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • <strong>Testing consent flows:</strong> Reset consent state to test authentication and authorization
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • <strong>Compliance audits:</strong> Review which applications have access to user data
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • <strong>Troubleshooting:</strong> Identify and fix consent-related authentication issues
                  </Typography>
                </Stack>
              </Box>
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Card>
    </Container>
  );
};

export default ConsentManager;
